<template>
  <div id="app" 
       :class="[
         {'is-mobile': getIsMobileLayout},
         {'is-sidebarvisible': getIsSidebarVisible}]">
    <div v-if="loading" id="loader" class="init-loader">
      <BrandImg
        class="init-loader__logo"
        src="logos/loading-icon.svg"
        alt="Logo" />
      <p class="init-loader__text">
        Loading...
      </p>
    </div>
    <div v-else id="main">
      <router-view />
    </div>

    <el-dialog
      title="Warning"
      :visible="getLogoutPopupStatus"
      :append-to-body="true"
      width="400px"
      @close="cancelLogout()"
      custom-class="logout-confirm"
      :show-close="false"
      center>
      <span class="logout-confirm__title">{{ $t('browserBackPressMessage') }}</span>
      <span slot="footer" class="dialog-footer">
        <el-button class="btn-cancel" @click.native="cancelLogout()">{{ $t('cancel') }}</el-button>
        <el-button class="btn-log-out" @click.native="logoutUserAction()">
          Log out
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { mapGetters, mapMutations } from 'vuex';
import loginMixins from '@m/user/mixins/login';
import { startAnalytics, updateBrand } from '@/utils/general';
import { getBrandConfig, getBrandEnv, getBrandKey, setEnv, getUIConfig } from '@/utils/env';
import BrandImg from '@/components/BrandImg.vue';
export default {
  mixins: [loginMixins],
  data() {
    return {
      loading: true,
      resizeTimer: false,
      resizeDelay: 250
    };
  },
  components: {
    BrandImg
  },
  created() {

    (async () => {
      // We recommend to call `load` at application startup.
      const fp = await FingerprintJS.load();

      // The FingerprintJS agent is ready.
      // Get a visitor identifier when you'd like to.
      const result = await fp.get();

      // This is the visitor identifier:
      const visitorId = result.visitorId;
      Vue.$http.defaults.headers.common['SD-Device-ID'] = visitorId;

      //after init
      /**
       * brand config cache at first time
       */
      // getBrandConfigFileMemo(getBrandKey()).then(() => {

      const isTestModeEnabled = getUIConfig('isTestModeEnabled', true) 
      let mode = process.env.VUE_APP_ENV; // Default build env
      if (mode == 'prod' && isTestModeEnabled == true) {
        mode = 'prodtest';
      }
      setEnv(mode);
      updateBrand();
      
      //set document title
      document.title = getBrandConfig('name', 'Solid');
      //set fav icon
      let link = document.querySelector('link[rel~=\'icon\']');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      const ico = getBrandKey();
      link.href = `/${ico}/${ico}.ico`;

      // this.isMobile();

      if(getBrandConfig('isSegmentEnabled', true) === true) {
        const segmentKey =  getBrandEnv('segmentKey');
        if(segmentKey && segmentKey !== '')
          startAnalytics(segmentKey);
      }

      this.loading = false;
    })();

    this.setIsMobileLayout(this.showResponsiveLayout());
    window.addEventListener('resize', this.checkResponsiveOnResize);
  },
  computed: {
    ...mapGetters(['getLogoutPopupStatus', 'getIsMobileLayout', 'getIsSidebarVisible'])
  },
  methods: {
    ...mapMutations(['updateLogoutPopupStatus', 'setIsMobileLayout', 'setIsSidebarVisible']),
    logoutUserAction() {
      this.logoutUser();
    },
    cancelLogout() {
      this.updateLogoutPopupStatus(false);
    },
    isMobile() {
      
      let checkIfMobile = false;
      const pageExclude = [ '/kyc/idv-callback'];
      // eslint-disable-next-line no-useless-escape
      if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0,4))) { 
        checkIfMobile = true;
      } else {
        checkIfMobile = false;
      }
      console.log(this.$route, window.location)
      if (checkIfMobile == true && !pageExclude.includes(window.location.pathname)) {
        this.$router.push('/welcome')
      }
    },
    showResponsiveLayout() {
      return document.documentElement.clientWidth <= 992;
    },
    checkResponsiveOnResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.setIsMobileLayout(this.showResponsiveLayout());
        if(!this.showResponsiveLayout()) {
          this.setIsSidebarVisible(false);
        }
      }, this.resizeDelay);
    }
  },
};
</script>

<style lang="scss">
.init-loader {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  &__logo {
    width: 100px;
    animation: translateY20 1s infinite alternate linear;
  }

  &__text {
    padding-top: 21px;
    font-weight: 700;
    font-size: 20px;
    font-weight: 700;
  }
}

.logout-confirm {
  border-radius: 4px;
  &.el-dialog {
    margin-top: 50vh !important;
    transform: translateY(-50%);
    border-radius: 4px;
  }

  .btn-cancel {
    border: 1px solid var(--branding);
    color: var(--branding);

    &:hover {
      border-color: #b9b9b9;
      background-color: #fff;
    }
  }

  .el-button {
    height: 38px;
    
    + .el-button {
      margin-left: 24px;
      background-color: var(--branding);
      color: #fff;
    }
  }

  .el-dialog__title {
    font-weight: 700;
  }

  .logout-confirm__title {
    color: var(--grey-1);
    word-break: break-word;
  }

  &.el-dialog--center .el-dialog__body {
    padding: 24px 24px 30px;
  }
}
</style>
